/**
 * 自动追踪状态管理 Store
 *
 * 管理 LLM 工作自动追踪功能的状态：
 * - 开关状态持久化
 * - 追踪目标记录
 * - 高亮效果管理
 * - 提供事件触发机制
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ==================== 类型定义 ====================

/** 追踪目标类型 */
export type TrackTargetType = 'setting' | 'chapter';

/** 追踪目标信息 */
export interface TrackTarget {
  type: TrackTargetType;
  id: number;
}

/** 高亮信息 */
export interface HighlightInfo {
  /** 目标 ID（章节 ID 或设定 ID） */
  targetId: number;
  /** 目标类型 */
  targetType: TrackTargetType;
  /** 高亮开始位置（字符偏移量，可选） */
  startOffset?: number;
  /** 高亮结束位置（字符偏移量，可选） */
  endOffset?: number;
  /** 高亮时间戳（用于触发动画） */
  timestamp: number;
  /** 高亮持续时间（毫秒） */
  duration: number;
}

/** 自动追踪状态接口 */
export interface AutoTrackState {
  /** 追踪开关状态 */
  enabled: boolean;
  /** 最后追踪的目标 */
  lastTrackedItem: TrackTarget | null;
  /** 当前高亮信息 */
  highlightInfo: HighlightInfo | null;

  // ===== 方法 =====
  /** 启用自动追踪 */
  enableAutoTrack: () => void;
  /** 禁用自动追踪 */
  disableAutoTrack: () => void;
  /** 切换自动追踪状态 */
  toggleAutoTrack: () => void;
  /** 设置追踪目标 */
  setTrackTarget: (type: TrackTargetType, id: number) => void;
  /** 清除追踪目标 */
  clearTrackTarget: () => void;
  /** 触发高亮效果 */
  triggerHighlight: (targetId: number, targetType: TrackTargetType, options?: {
    startOffset?: number;
    endOffset?: number;
    duration?: number;
  }) => void;
  /** 清除高亮效果 */
  clearHighlight: () => void;
}

// ==================== 常量定义 ====================

const STORAGE_KEY = 'autoTrack';
const DEFAULT_HIGHLIGHT_DURATION = 2500; // 默认高亮持续时间 2.5 秒

// ==================== 创建 Store ====================

export const useAutoTrackStore = create<AutoTrackState>()(
  persist(
    (set) => ({
      // ===== 初始状态 =====
      enabled: false,
      lastTrackedItem: null,
      highlightInfo: null,

      // ===== 方法实现 =====

      enableAutoTrack: () => {
        set((state) => {
          if (state.enabled) return state;
          return { enabled: true };
        });
      },

      disableAutoTrack: () => {
        set((state) => {
          if (!state.enabled) return state;
          return { enabled: false };
        });
      },

      toggleAutoTrack: () => {
        set((state) => ({
          enabled: !state.enabled
        }));
      },

      setTrackTarget: (type: TrackTargetType, id: number) => {
        set({
          lastTrackedItem: { type, id }
        });
      },

      clearTrackTarget: () => {
        set({ lastTrackedItem: null });
      },

      triggerHighlight: (targetId: number, targetType: TrackTargetType, options?: {
        startOffset?: number;
        endOffset?: number;
        duration?: number;
      }) => {
        const highlightInfo: HighlightInfo = {
          targetId,
          targetType,
          startOffset: options?.startOffset,
          endOffset: options?.endOffset,
          timestamp: Date.now(),
          duration: options?.duration ?? DEFAULT_HIGHLIGHT_DURATION
        };
        set({ highlightInfo });

        // 自动清除高亮
        setTimeout(() => {
          set((state) => {
            // 只有当高亮信息未变化时才清除（防止覆盖新的高亮）
            if (state.highlightInfo?.timestamp === highlightInfo.timestamp) {
              return { highlightInfo: null };
            }
            return state;
          });
        }, highlightInfo.duration + 500); // 额外 500ms 确保动画完成
      },

      clearHighlight: () => {
        set({ highlightInfo: null });
      }
    }),
    {
      name: STORAGE_KEY,
      // 只持久化 enabled 状态
      partialize: (state) => ({
        enabled: state.enabled
      })
    }
  )
);

// ==================== 事件辅助函数 ====================

/** 追踪事件详情类型 */
export interface TrackEventDetail {
  /** 目标 ID */
  targetId: number;
  /** 高亮开始位置（可选） */
  startOffset?: number;
  /** 高亮结束位置（可选） */
  endOffset?: number;
}

/**
 * 触发设定追踪事件
 */
export function triggerSettingTrack(settingId: number, highlightOptions?: {
  startOffset?: number;
  endOffset?: number;
}): void {
  const detail: TrackEventDetail = {
    targetId: settingId,
    ...highlightOptions
  };
  window.dispatchEvent(new CustomEvent('autoTrack:setting', { detail }));
}

/**
 * 触发章节追踪事件
 */
export function triggerChapterTrack(chapterId: number, highlightOptions?: {
  startOffset?: number;
  endOffset?: number;
}): void {
  console.log('[triggerChapterTrack] Called with chapterId:', chapterId, 'highlightOptions:', highlightOptions);
  const detail: TrackEventDetail = {
    targetId: chapterId,
    ...highlightOptions
  };
  window.dispatchEvent(new CustomEvent('autoTrack:chapter', { detail }));
}

export default useAutoTrackStore;